'use client';

import React, { useState } from 'react';
import { IHireAiHero } from '@/components/type/hireUs/hireAiType';
import Button from '@/components/ui/Button';

export const ArrowUpRight = () => (
  <svg
    width="1em"
    height="1em"
    viewBox="0 0 12 12"
    fill="none"
    aria-hidden="true"
    className="shrink-0"
  >
    <path
      d="M2.5 9.5L9.5 2.5M9.5 2.5H3.8M9.5 2.5V8.2"
      stroke="currentColor"
      strokeWidth="1.3"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const fieldBase =
  'w-full border-b border-white/25 bg-transparent text-sm text-white outline-none transition-colors placeholder:text-white/70 focus:border-white min-[1600px]:text-base';
const inputClass = `${fieldBase} h-14 min-[1600px]:h-[72px]`;
const textareaClass = `${fieldBase} min-h-[96px] resize-none pt-5 min-[1600px]:min-h-[108px]`;

type FormStatus = 'idle' | 'sending' | 'success' | 'error';

type Props = {
  form: IHireAiHero['form'];
};

const HireAiHeroForm = ({ form }: Props) => {
  const [status, setStatus] = useState<FormStatus>('idle');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const formEl = e.currentTarget;
    const values = Object.fromEntries(new FormData(formEl));

    setStatus('sending');
    try {
      const res = await fetch('/api/hire-ai-lead', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...values,
          formTitle: form.title,
          pageUrl: window.location.href,
        }),
      });
      if (!res.ok) throw new Error('Request failed');
      formEl.reset();
      setStatus('success');
    } catch {
      setStatus('error');
    }
  };

  return (
    <div className="flex w-full flex-col gap-6 rounded-2xl border border-white/10 bg-gradient-to-br from-white/[0.08] to-[#0a1a30]/70 p-6 shadow-2xl backdrop-blur-md sm:p-8 lg:w-[44%] lg:max-w-[560px] lg:shrink-0 min-[1600px]:max-w-[658px] min-[1600px]:gap-8 min-[1600px]:p-10">
      <div className="flex flex-col gap-2">
        <h2 className="font-opt text-xl font-medium text-white min-[1600px]:text-2xl">
          {form.title}
        </h2>
        <p className="font-opt text-sm leading-5 text-white/70 min-[1600px]:text-base min-[1600px]:leading-6">
          {form.description}
        </p>
      </div>

      {status === 'success' ? (
        <div className="flex flex-col items-start gap-3 rounded-lg bg-white/10 p-4 font-opt text-sm text-white">
          <p>Thanks! We will share matched profiles with you shortly.</p>
          <button
            type="button"
            onClick={() => setStatus('idle')}
            className="underline underline-offset-4 hover:opacity-80"
          >
            Send another enquiry
          </button>
        </div>
      ) : (
        <form
          onSubmit={handleSubmit}
          className="flex flex-col gap-6 min-[1600px]:gap-10"
        >
          {/* honeypot: bots fill it, humans never see it */}
          <input
            type="text"
            name="website"
            tabIndex={-1}
            autoComplete="off"
            aria-hidden="true"
            className="absolute -left-[9999px] h-0 w-0 opacity-0"
          />

          <div className="flex flex-col">
            <div className="grid grid-cols-1 gap-x-6 sm:grid-cols-2 min-[1600px]:gap-x-10">
              <input
                name="firstName"
                required
                maxLength={60}
                placeholder="First Name"
                className={inputClass}
              />
              <input
                name="lastName"
                required
                maxLength={60}
                placeholder="Last Name"
                className={inputClass}
              />
            </div>

            <div className="grid grid-cols-1 gap-x-6 sm:grid-cols-2 min-[1600px]:gap-x-10">
              <input
                name="email"
                type="email"
                required
                maxLength={120}
                placeholder="Email Address"
                className={inputClass}
              />
              <input
                name="phone"
                type="tel"
                required
                maxLength={20}
                placeholder="Contact Number"
                className={inputClass}
              />
            </div>

            <textarea
              name="message"
              rows={3}
              maxLength={1000}
              placeholder="Type your message here..."
              className={textareaClass}
            />
          </div>

          {status === 'error' && (
            <p role="alert" className="font-opt text-sm text-[#ff8a7a]">
              Something went wrong. Please try again, or email us directly.
            </p>
          )}

          <Button
            btnName={form.submitBtn}
            isButton
            type="submit"
            isBackgroundLight
            isLoading={status === 'sending'}
          />
        </form>
      )}
    </div>
  );
};

export default HireAiHeroForm;
